// components/CommandPalette/utils/commandParser.ts
//
// Command-palette parser for the natural-language command bar. Phase 6.4
// (2026-05-11) made the course-code regex institution-aware: the 13
// command patterns are built from a per-slug course-code pattern rather
// than hardcoded to UMD's 4-letter-dept + 3-digit-number form. Callers
// pass `config.slug` from `useInstitutionConfig()`; unknown slugs fall
// back to UMD's pattern.

export interface ParsedCommandRequirements {
  department?: string;
  levelMin?: number;
  levelMax?: number;
  genEdCategories?: string[];
  keywords?: string[];
}

export type ParsedCommand =
  | { type: 'ADD_MAJOR'; programCode: string }
  | { type: 'ADD_MINOR'; programCode: string }
  | { type: 'ADD_CITATION'; programCode: string }
  | { type: 'ADD_SEMESTER'; semester: string | null }
  | { type: 'ADD'; courseCode: string; semester: string | null }
  | { type: 'REMOVE_MAJOR'; programCode: string }
  | { type: 'REMOVE_MINOR'; programCode: string }
  | { type: 'REMOVE_CITATION'; programCode: string }
  | { type: 'REMOVE_SEMESTER'; semester: string | null }
  | { type: 'REMOVE'; courseCode: string; semester: string | null }
  | { type: 'MOVE'; courseCode: string; fromSemester: string | null; toSemester: string | null }
  | { type: 'ACCEPT'; courseCode: string }
  | { type: 'REJECT'; courseCode: string }
  | { type: 'BOOKMARK'; courseCode: string }
  | { type: 'UNBOOKMARK'; courseCode: string }
  | { type: 'PIN'; courseCode: string; semester: string | null }
  | { type: 'UNPIN'; courseCode: string; semester: string | null }
  | { type: 'RENAME_PLAN'; name: string }
  | {
      type: 'CREATE_PLACEHOLDER';
      title: string;
      semester: string | null;
      requirements: ParsedCommandRequirements;
    }
  | { type: 'GENERATE_PLAN' }
  | { type: 'SUGGEST' }
  | { type: 'STRATEGY'; strategy: string }
  | { type: 'PREFER'; courses: string }
  | { type: 'EXPLAIN'; courseCode: string }
  | { type: 'WHAT_IF'; query: string }
  | { type: 'PREREQS'; courseCode: string }
  | { type: 'AVAILABLE'; courseCode: string }
  | { type: 'INFO'; courseCode: string }
  | { type: 'SEARCH'; query: string }
  | { type: 'STATUS' }
  | { type: 'CLEAR_PREFERENCES' }
  | { type: 'CLEAR_SUGGESTIONS' }
  | { type: 'CLEAR_SEMESTER'; semester: string | null }
  | { type: 'CLEAR_ALL' }
  | { type: 'LIST'; what: string; semester: string | null }
  | { type: 'HELP' };

export interface ParseError {
  error: string;
}

export type ParseResult = ParsedCommand | ParseError;

// Per-institution course-code pattern (as a regex string fragment).
// Match the backend `services.course.code` helpers — UMD: strict
// 4-letter dept + 3-digit number + optional 1-letter suffix; SJSU:
// 2-4 letter dept + optional whitespace + 1-3 digit number + optional
// suffix. The command parser normalizes its input to uppercase and
// matches against these patterns at command-recognition time.
const DEFAULT_SLUG = 'umd';
const COURSE_CODE_PATTERN_BY_SLUG: Record<string, string> = {
  umd: '[A-Z]{4}\\d{3}[A-Z]?',
  // \d? for digit departments (SJSU splits Business into BUS1..BUS5) and a
  // suffix of up to THREE letters (ART 44HA, NURS 100WE, CHHS 198ITL,
  // BIOL 255MMP) — a shorter form truncates them in free text
  // ('CHHS 198ITL' -> 'CHHS 198IT'). Mirrors backend services/course/code.py.
  sjsu: '[A-Z]{2,4}\\d?\\s?\\d{1,3}[A-Z]{0,3}',
  // UC Merced: like SJSU but pure-alpha dept; accepts padded ('CSE 031')
  // and unpadded ('CSE 31') input — the backend canonicalizes to padded.
  // Suffix up to TWO letters (PHYS 008HL honors labs, BIO 127LA/LB —
  // S4 Layer-0 finding 2026-07-24; a 1-letter pattern truncates 008HL
  // to the different course 008H).
  ucmerced: '[A-Z]{2,4}\\s?\\d{1,3}[A-Z]{0,2}',
  // SDSU (W44 S3): 26 subject prefixes contain an internal space (E E 210,
  // POL S 102, CIV E 121) — closed alternation first (space REQUIRED there,
  // 'AS101' would be ambiguous), generic 2-5 letters with optional space
  // after ('CS150' ok); 3-digit numbers + optional 1-letter suffix. Mirrors
  // backend/services/course/code.py _SDSU_SCAN_RE.
  sdsu: '(?:A E|A S|B A|C P|CIV E|CON E|CON M|E E|ENV E|ENV S|GEN S|H SEC|I B|INT S|LIB S|M BIO|M E|M S E|MIL S|N SCI|NAV S|P A|P H|POL S|R A|REL S)\\s\\d{3}[A-Z]?|[A-Z]{2,5}\\s?\\d{3}[A-Z]?',
  // De Anza (W49 A7 tranche 2): 2-4 letter prefix OR the F/TV slash prefix
  // (the catalog's only one) + space + legacy 1-3 digit number or CCN
  // C+4 digits (bare 4-digit surface form accepted; backend normalizes to
  // the C form) + up to a 3-letter suffix (F/TV 2AWH). Mirrors
  // backend/services/course/code.py _DEANZA_SCAN_RE.
  deanza: '(?:F/TV|[A-Z]{2,4})\\s+C?\\d{1,4}[A-Z]{0,3}',
  // Merced College (W49 A7 tranche 2): always hyphenated — CCN C+4 digits,
  // local 2-3 digit zero-padded numbers, or the digitless MATH-C; up to 2
  // suffix letters. Mirrors backend _MERCED_SCAN_RE.
  'merced-college': '[A-Z]{3,4}-(?:C\\d{4}[A-Z]?|\\d{1,3}[A-Z]{0,2}|[A-Z])',
  // Montgomery College, MD (W52 D4): 4 letters + 3 digits. The space is
  // canonical but optional in typed input (the backend canonicalizes
  // 'CMSC216' to 'CMSC 216' under this institution). An optional applied-music
  // instrument symbol (MUSC 145E) is let through here; the backend accepts it
  // only on the six MUSC individual-instruction courses and maps it to the base.
  'montgomery-college': '[A-Z]{4}\\s?\\d{3}(?:KK|KV|MM|QQ|UU|VV|[A-RUV])?',
  // Foothill (W52 D4): six two-letter spaced prefixes (C S, D A, D H, L A,
  // R T, V T) in a closed alternation FIRST with the space required; every
  // other prefix is 3-4 letters with the space optional. Local 1-3 digits or
  // CCN C+4, up to a 3-letter suffix (MATH 1AHP). Mirrors backend
  // _FOOTHILL_SCAN_RE.
  foothill: '(?:C S|D A|D H|L A|R T|V T)\\s(?:C\\d{4}|\\d{1,3})[A-Z]{0,3}|[A-Z]{3,4}\\s?(?:C\\d{4}|\\d{1,3})[A-Z]{0,3}',
  // University of Nicosia: 3-5 letters + hyphen + 3-4 digits + up to a 3-letter suffix
  // (PSD-110G, BADM-231DL — distinct courses). The hyphen is canonical but optional in
  // typed input ('COMP 111', 'COMP111'); the backend canonicalizes to 'COMP-111'.
  // Mirrors backend _UNIC_PARSE_RE.
  // Closed 2-digit exception: BENG-60 / BENG-70 (preparatory English).
  unic: 'BENG[-\\s]?\\d{2}|[A-Z]{3,5}[-\\s]?\\d{3,4}[A-Z]{0,3}',
  // Mission College (W54 D1): 3-4 letters + optional space + a ZERO-PADDED
  // 3-digit local number ('ACC 001A', 'MAT 000D' — never 'ACC 1A'), a 4-digit
  // baccalaureate number in the 3xxx/4xxx family ('COMM 3401'; 1xxx/2xxx is
  // excluded so 'FALL 2026' is not a code) or CCN C+4 ('ENGL C1000'), plus up
  // to a 2-letter suffix. Mirrors backend _MISSION_SCAN_RE.
  'mission-college': '[A-Z]{3,4}\\s?(?:C\\d{4}|[34]\\d{3}|\\d{3})[A-Z]{0,2}',
  // San José City College (W54 D1): 2-5 letters + optional space + a
  // ZERO-PADDED 3-digit local number ('MATH 013'; a typed 'MATH 13' is let
  // through and the backend zfills it), CCN C+4 ('ENGL C1000'), the one bare
  // 4-digit local with its suffix (STAT 1000X — never 'FALL 2026') or the one
  // letter-led local (COS X109), plus up to a 2-letter suffix. Mirrors backend
  // _SJCC_SCAN_RE.
  sjcc: '[A-Z]{2,5}\\s?(?:C\\d{4}[A-Z]{0,2}|\\d{4}[A-Z]{1,2}|X\\d{3}[A-Z]{0,2}|\\d{1,3}[A-Z]{0,2})',
  // Evergreen Valley College (W54 D1): 2-5 letters (seven 2-letter prefixes:
  // AI AJ II LA SL TI WE) + optional space + a ZERO-PADDED 3-digit local
  // number ('MATH 013'; a typed 'MATH 13' is let through and the backend
  // zfills it), CCN C+4 ('ENGL C1000') or the one 4-digit local in the 1xxx
  // block (STAT 1000 — never 'FALL 2026'), plus an optional 1-letter suffix.
  // Mirrors backend _EVC_SCAN_RE.
  evc: '[A-Z]{2,5}\\s?(?:C\\d{4}|1\\d{3}|\\d{1,3})[A-Z]?',
  // UC Davis (W56 D1): REUSES the UC Merced key (ucm_dept_space_padnum) —
  // 3-letter subjects, zero-padded 3-digit numbers, up to two suffix letters
  // (ECS 036A, MAT 021AH, AAS 194HA, WLD 910); unpadded typed input
  // ('ECS 36A') is let through and the backend zfills it. Identical to the
  // ucmerced row by construction; slug-keyed because the map is per slug.
  ucdavis: '[A-Z]{2,4}\\s?\\d{1,3}[A-Z]{0,2}',
  // CSU East Bay (W56 D1): 2-4 unspaced letters + optional space + exactly
  // 3 digits + optional 1-letter suffix ('ANTH 110L', 'CS 401', 'ENGL 300W');
  // the unspaced typed form ('CS401') is let through and the backend
  // canonicalizes it. Mirrors backend _CSUEB_SCAN_RE.
  csueastbay: '[A-Z]{2,4}\\s?[1-9]\\d{2}[A-Z]?',
  // UC Berkeley (W56 D1): the 38 legacy spaced/punctuated prose subjects of
  // the retired CourseLeaf guide (space REQUIRED there; the backend resolves
  // 'MEC ENG C85' to 'MECENG C85'), then a generic 2-7 unspaced letters with
  // optional space ('COMPSCI 61A', 'COMPSCI61A'); number = optional [CHNRWS]
  // prefix + 1-3 never-padded digits + 0-2 suffix letters. The closed list of
  // 245 subjects is enforced by the backend parser (_UCB_SUBJECTS), not here.
  // A typed legacy or compact form reaches the executor, whose lookup
  // compares codes after normalizeTypedCode() (spaces, ',' and '&' dropped),
  // so 'MEC ENG C85' / 'COMPSCI61A' find the catalog's 'MECENG C85' /
  // 'COMPSCI 61A' when the local list or the search carries them; a subject
  // the closed list lacks is rejected by the backend. Mirrors backend
  // _UCB_SCAN_RE.
  ucberkeley: '(?:A,RESEC|AERO ENG|BIO ENG|CHM ENG|CIV ENG|COG SCI|COM LIT|CRIT TH|CY PLAN|DES INV|DEV ENG|DEV STD|EA LANG|EL ENG|ENE,RES|ENV DES|ETH STD|IND ENG|L&S|LAN PRO|LD ARCH|M E STU|MAT SCI|MEC ENG|MED ST|MIL AFF|MIL SCI|NAT RES|NAV SCI|NUC ENG|PB HLTH|PHYS ED|POL SCI|PUB AFF|PUB POL|SOC WEL|VIS SCI|VIS STD)\\s[CHNRWS]?\\d{1,3}[A-Z]{0,2}|[A-Z]{2,7}\\s?[CHNRWS]?\\d{1,3}[A-Z]{0,2}',
};

// Memoize per-slug builds so the parser doesn't rebuild 13 RegExps on
// every keystroke. The set is at most one entry per institution served
// by this frontend deploy.
const COMMANDS_BY_SLUG = new Map<string, Record<string, RegExp>>();

function buildCommands(institutionSlug: string): Record<string, RegExp> {
  const cached = COMMANDS_BY_SLUG.get(institutionSlug);
  if (cached) return cached;

  const code = COURSE_CODE_PATTERN_BY_SLUG[institutionSlug]
    ?? COURSE_CODE_PATTERN_BY_SLUG[DEFAULT_SLUG];
  const C = `(${code})`; // capture group around the course-code pattern

  // Order matters: more specific patterns must come before general ones.
  // Patterns mirror the pre-Phase-6.4 hardcoded UMD set; only the
  // course-code subpattern is templated.
  const commands: Record<string, RegExp> = {
    ADD_MAJOR: /^ADD\s+MAJOR\s+(\S+)$/i,
    ADD_MINOR: /^ADD\s+MINOR\s+(\S+)$/i,
    ADD_CITATION: /^ADD\s+CITATION\s+(\S+)$/i,
    ADD_SEMESTER: /^ADD\s+SEMESTER\s+([\w-]+)$/i,
    ADD: new RegExp(`^ADD\\s+${C}\\s+TO\\s+([\\w-]+)$`, 'i'),
    REMOVE_MAJOR: /^REMOVE\s+MAJOR\s+(\S+)$/i,
    REMOVE_MINOR: /^REMOVE\s+MINOR\s+(\S+)$/i,
    REMOVE_CITATION: /^REMOVE\s+CITATION\s+(\S+)$/i,
    REMOVE_SEMESTER: /^REMOVE\s+SEMESTER\s+([\w-]+)$/i,
    REMOVE: new RegExp(`^REMOVE\\s+${C}\\s+FROM\\s+([\\w-]+)$`, 'i'),
    MOVE: new RegExp(`^MOVE\\s+${C}\\s+FROM\\s+([\\w-]+)\\s+TO\\s+([\\w-]+)$`, 'i'),
    ACCEPT: new RegExp(`^ACCEPT\\s+${C}$`, 'i'),
    REJECT: new RegExp(`^REJECT\\s+${C}$`, 'i'),
    BOOKMARK: new RegExp(`^BOOKMARK\\s+${C}$`, 'i'),
    UNBOOKMARK: new RegExp(`^UNBOOKMARK\\s+${C}$`, 'i'),
    PIN: new RegExp(`^PIN\\s+${C}\\s+IN\\s+([\\w-]+)$`, 'i'),
    UNPIN: new RegExp(`^UNPIN\\s+${C}\\s+IN\\s+([\\w-]+)$`, 'i'),
    RENAME_PLAN: /^RENAME\s+PLAN\s+"([^"]+)"$/i,
    CREATE_PLACEHOLDER: /^CREATE\s+PLACEHOLDER\s+"([^"]+)"\s+IN\s+([\w-]+)(?:\s+WITH\s+(.+))?$/i,
    GENERATE_PLAN: /^GENERATE\s+PLAN$/i,
    SUGGEST: /^SUGGEST(?:\s+COURSES)?$/i,
    STRATEGY: /^STRATEGY\s+(LIGHTER|NORMAL|HEAVIER)$/i,
    PREFER: /^PREFER\s+(.+)$/i,
    EXPLAIN: new RegExp(`^EXPLAIN\\s+${C}$`, 'i'),
    WHAT_IF: /^WHAT\s+IF\s+(.+)$/i,
    PREREQS: new RegExp(`^PREREQS\\s+${C}$`, 'i'),
    AVAILABLE: new RegExp(`^AVAILABLE\\s+${C}$`, 'i'),
    INFO: new RegExp(`^INFO\\s+${C}$`, 'i'),
    SEARCH: /^SEARCH\s+(.+)$/i,
    STATUS: /^STATUS$/i,
    CLEAR_PREFERENCES: /^CLEAR\s+PREFERENCES$/i,
    CLEAR_SUGGESTIONS: /^CLEAR\s+SUGGESTIONS$/i,
    CLEAR_SEMESTER: /^CLEAR\s+([\w-]+)$/i,
    CLEAR_ALL: /^CLEAR\s+ALL$/i,
    LIST: /^LIST\s+(COURSES|SEMESTERS|BOOKMARKS|PROGRAMS)(?:\s+IN\s+([\w-]+))?$/i,
    HELP: /^HELP$/i,
  };
  COMMANDS_BY_SLUG.set(institutionSlug, commands);
  return commands;
}

const SEMESTER_ALIASES: Record<string, string> = {
  F23: 'fall-2023',
  W24: 'winter-2024',
  S24: 'spring-2024',
  SU24: 'summer-2024',
  F24: 'fall-2024',
  W25: 'winter-2025',
  S25: 'spring-2025',
  SU25: 'summer-2025',
  F25: 'fall-2025',
  W26: 'winter-2026',
  S26: 'spring-2026',
  SU26: 'summer-2026',
  F26: 'fall-2026',
  W27: 'winter-2027',
  S27: 'spring-2027',
  SU27: 'summer-2027',
  F27: 'fall-2027',
  W28: 'winter-2028',
  S28: 'spring-2028',
  SU28: 'summer-2028',
  F28: 'fall-2028',
  W29: 'winter-2029',
  S29: 'spring-2029',
  SU29: 'summer-2029',
  F29: 'fall-2029',
  W30: 'winter-2030',
  S30: 'spring-2030',
  SU30: 'summer-2030',
  F30: 'fall-2030',
  'FALL-2024': 'fall-2024',
  'WINTER-2025': 'winter-2025',
  'SPRING-2025': 'spring-2025',
  'SUMMER-2025': 'summer-2025',
};

export function parseCommand(
  input: string,
  institutionSlug: string = DEFAULT_SLUG,
): ParseResult {
  const trimmed = input.trim().toUpperCase();
  const commands = buildCommands(institutionSlug);

  for (const [commandType, pattern] of Object.entries(commands)) {
    const match = trimmed.match(pattern);
    if (match) {
      switch (commandType) {
        case 'ADD_MAJOR':
          return { type: 'ADD_MAJOR', programCode: match[1] };

        case 'ADD_MINOR':
          return { type: 'ADD_MINOR', programCode: match[1] };

        case 'ADD_CITATION':
          return { type: 'ADD_CITATION', programCode: match[1] };

        case 'ADD_SEMESTER':
          return { type: 'ADD_SEMESTER', semester: normalizeSemester(match[1]) };

        case 'ADD':
          return { type: 'ADD', courseCode: match[1], semester: normalizeSemester(match[2]) };

        case 'REMOVE_MAJOR':
          return { type: 'REMOVE_MAJOR', programCode: match[1] };

        case 'REMOVE_MINOR':
          return { type: 'REMOVE_MINOR', programCode: match[1] };

        case 'REMOVE_CITATION':
          return { type: 'REMOVE_CITATION', programCode: match[1] };

        case 'REMOVE_SEMESTER':
          return { type: 'REMOVE_SEMESTER', semester: normalizeSemester(match[1]) };

        case 'REMOVE':
          return { type: 'REMOVE', courseCode: match[1], semester: normalizeSemester(match[2]) };

        case 'MOVE':
          return {
            type: 'MOVE',
            courseCode: match[1],
            fromSemester: normalizeSemester(match[2]),
            toSemester: normalizeSemester(match[3]),
          };

        case 'ACCEPT':
          return { type: 'ACCEPT', courseCode: match[1] };

        case 'REJECT':
          return { type: 'REJECT', courseCode: match[1] };

        case 'BOOKMARK':
          return { type: 'BOOKMARK', courseCode: match[1] };

        case 'UNBOOKMARK':
          return { type: 'UNBOOKMARK', courseCode: match[1] };

        case 'PIN':
          return { type: 'PIN', courseCode: match[1], semester: normalizeSemester(match[2]) };

        case 'UNPIN':
          return { type: 'UNPIN', courseCode: match[1], semester: normalizeSemester(match[2]) };

        case 'RENAME_PLAN':
          return { type: 'RENAME_PLAN', name: match[1] };

        case 'CREATE_PLACEHOLDER':
          return {
            type: 'CREATE_PLACEHOLDER',
            title: match[1],
            semester: normalizeSemester(match[2]),
            requirements: match[3] ? parseRequirements(match[3]) : {},
          };

        case 'GENERATE_PLAN':
          return { type: 'GENERATE_PLAN' };

        case 'SUGGEST':
          return { type: 'SUGGEST' };

        case 'STRATEGY':
          return { type: 'STRATEGY', strategy: match[1].toLowerCase() };

        case 'PREFER':
          return { type: 'PREFER', courses: match[1] };

        case 'EXPLAIN':
          return { type: 'EXPLAIN', courseCode: match[1] };

        case 'WHAT_IF':
          return { type: 'WHAT_IF', query: match[1] };

        case 'PREREQS':
          return { type: 'PREREQS', courseCode: match[1] };

        case 'AVAILABLE':
          return { type: 'AVAILABLE', courseCode: match[1] };

        case 'INFO':
          return { type: 'INFO', courseCode: match[1] };

        case 'SEARCH':
          return { type: 'SEARCH', query: match[1] };

        case 'STATUS':
          return { type: 'STATUS' };

        case 'CLEAR_PREFERENCES':
          return { type: 'CLEAR_PREFERENCES' };

        case 'CLEAR_SUGGESTIONS':
          return { type: 'CLEAR_SUGGESTIONS' };

        case 'CLEAR_SEMESTER':
          return { type: 'CLEAR_SEMESTER', semester: normalizeSemester(match[1]) };

        case 'CLEAR_ALL':
          return { type: 'CLEAR_ALL' };

        case 'LIST':
          return {
            type: 'LIST',
            what: match[1],
            semester: match[2] ? normalizeSemester(match[2]) : null,
          };

        case 'HELP':
          return { type: 'HELP' };

        default:
          break;
      }
    }
  }

  return {
    error: `Invalid command: "${input}". Type HELP for available commands.`,
  };
}

function normalizeSemester(input: string): string | null {
  const upper = input.toUpperCase();

  const transferAliases = ['TRANSFER', 'TRANSFERS', 'TRANSFER-CREDITS', 'TRANSFER CREDITS', 'AP', 'APS'];
  if (transferAliases.includes(upper.replace(/ /g, '-'))) {
    return 'transfer';
  }

  if (SEMESTER_ALIASES[upper]) {
    return SEMESTER_ALIASES[upper];
  }

  const fullMatch = upper.match(/^(FALL|WINTER|SPRING|SUMMER)-(\d{4})$/);
  if (fullMatch) {
    return `${fullMatch[1].toLowerCase()}-${fullMatch[2]}`;
  }

  if (/^(fall|winter|spring|summer)-\d{4}$/.test(input.toLowerCase())) {
    return input.toLowerCase();
  }

  return null;
}

function parseRequirements(reqString: string): ParsedCommandRequirements {
  const requirements: ParsedCommandRequirements = {};
  const parts = reqString.split(/\s+/);

  parts.forEach((part) => {
    const [key, value] = part.split(':');
    if (key && value) {
      switch (key.toUpperCase()) {
        case 'DEPT':
        case 'DEPARTMENT':
          requirements.department = value;
          break;
        case 'LEVEL': {
          const [min, max] = value.split('-');
          if (min) requirements.levelMin = parseInt(min, 10);
          if (max) requirements.levelMax = parseInt(max, 10);
          break;
        }
        case 'GENED':
        case 'GENEDS':
          requirements.genEdCategories = value.split(',');
          break;
        case 'KEYWORDS':
          requirements.keywords = value.split(',');
          break;
        default:
          break;
      }
    }
  });

  return requirements;
}

export function getCommandSuggestions(input: string, _academicPlan?: unknown): string[] {
  const suggestions: string[] = [];
  const upper = input.toUpperCase();

  if (upper.startsWith('ADD ')) {
    suggestions.push(
      'ADD [COURSE] TO [SEMESTER]',
      'ADD MAJOR [CODE]',
      'ADD MINOR [CODE]',
      'ADD CITATION [CODE]',
      'ADD SEMESTER [SEMESTER]'
    );
  } else if (upper.startsWith('REMOVE ')) {
    suggestions.push(
      'REMOVE [COURSE] FROM [SEMESTER]',
      'REMOVE MAJOR [CODE]',
      'REMOVE MINOR [CODE]',
      'REMOVE CITATION [CODE]',
      'REMOVE SEMESTER [SEMESTER]'
    );
  } else if (upper.startsWith('LIST ')) {
    suggestions.push(
      'LIST SEMESTERS',
      'LIST COURSES IN [SEMESTER]',
      'LIST BOOKMARKS',
      'LIST PROGRAMS'
    );
  } else if (upper.startsWith('MOVE ')) {
    suggestions.push('MOVE [COURSE] FROM [SEMESTER] TO [SEMESTER]');
  } else if (upper.startsWith('CREATE ')) {
    suggestions.push('CREATE PLACEHOLDER "[TITLE]" IN [SEMESTER]');
  } else if (!input) {
    suggestions.push(
      'ADD [COURSE] TO [SEMESTER]',
      'REMOVE [COURSE] FROM [SEMESTER]',
      'MOVE [COURSE] FROM [SEM] TO [SEM]',
      'INFO [COURSE]',
      'SEARCH [QUERY]',
      'STATUS',
      'HELP'
    );
  }

  return suggestions.slice(0, 5);
}
